import React, { useMemo } from "react";
import { StyleSheet, View } from "react-native";
import Animated, {
  useSharedValue,
  useAnimatedStyle,
  runOnJS,
  withTiming,
} from "react-native-reanimated";
import { Gesture, GestureDetector } from "react-native-gesture-handler";
import { widthPercentageToDP as wp } from "react-native-responsive-screen";
import clamp from "app/utils/animation/clamp";
import SliderLabels from "./SliderLabels";
import { Box } from "../Box/MainBox/MainBox";
import theme from "app/Theme/Theme";
import { isWebDesktop } from "@microdoing/shared/utils/deviceDetectors";
import normalize from "app/utils/normalize";
import { body } from "app/Theme/Text/Text";

const CONTAINER_HEIGHT = 30;

const SLIDER_WIDTH = isWebDesktop ? normalize(344) : wp("80%");
const SLIDER_HEIGHT = 4;
const SLIDER_RADIUS = 8;

const KNOB_WIDTH = 20;

const NUMBER_OF_STEPS = 10;

const TOTAL_VALUE = SLIDER_WIDTH - KNOB_WIDTH;

const shadowStyle = {
  shadowColor: "#3C4257",
  shadowOffset: {
    width: 0,
    height: 2,
  },
  shadowOpacity: 0.15,
  shadowRadius: 2.5,
  elevation: 3,
};

const styles = StyleSheet.create({
  container: {
    marginTop: 5,
    width: SLIDER_WIDTH,
    paddingVertical: 10,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    height: CONTAINER_HEIGHT,
  },
  slider: {
    height: SLIDER_HEIGHT,
    width: SLIDER_WIDTH,
    borderRadius: SLIDER_RADIUS,
    backgroundColor: theme.colors["neutral.350"],
    justifyContent: "center",
  },
  progress: {
    ...StyleSheet.absoluteFillObject,
    height: SLIDER_HEIGHT,
    width: SLIDER_WIDTH - KNOB_WIDTH,
    borderRadius: SLIDER_RADIUS,
    backgroundColor: theme.colors["brand.500"],
  },
  knob: {
    width: KNOB_WIDTH,
    height: KNOB_WIDTH,
    borderRadius: KNOB_WIDTH / 2,
    borderWidth: 2,
    borderColor: theme.colors["neutral.white"],
    backgroundColor: theme.colors["brand.500"],
    ...shadowStyle,
  },
  numbers: {
    width: SLIDER_WIDTH + KNOB_WIDTH / 2,
    alignItems: "center",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  labelSelected: {
    ...body["l4-bold"],
    color: theme.colors["neutral.900"],
  },
  label: {
    textAlign: "center",
    width: SLIDER_WIDTH / (NUMBER_OF_STEPS + 1),
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexDirection: "column",
    ...body["l4-regular"],
    color: theme.colors["neutral.400"],
  },
});

interface Props {
  style?: {};
  value: number;
  minimumValue?: number;
  maximumValue?: number;
  step?: number;
  thumbTintColor?: string;
  minimumTrackTintColor?: string;
  onValueChange: (stepValue: number) => void;
  hasLabels?: boolean;
  hasNumbers?: boolean;
  hasCustomValue?: boolean;
  leftLabel: string;
  rightLabel: string;
}

const Slider = ({
  style = {},
  hasCustomValue = false,
  minimumValue = 0,
  maximumValue = 10,
  step = 1,
  hasLabels = true,
  onValueChange = stepValue => null,
  value = 5,
  rightLabel,
  leftLabel,
  hasNumbers = true,
}: Props) => {
  const numbers = useMemo(
    () =>
      new Array(NUMBER_OF_STEPS + 1)
        .fill(1)
        .map((el, i) => (hasCustomValue ? minimumValue + i : i)),
    [hasCustomValue, minimumValue],
  );

  const initialTranslate = hasCustomValue
    ? numbers.findIndex(el => el === value)
    : value;

  const translateX = useSharedValue(
    (initialTranslate * TOTAL_VALUE) / NUMBER_OF_STEPS,
  );

  const previousTranslateX = useSharedValue(
    (initialTranslate * TOTAL_VALUE) / NUMBER_OF_STEPS,
  );

  const stepValue = useSharedValue(value);

  const tapGesture = Gesture.Tap().onStart(event => {
    const mooveX = clamp(event.x, 0, SLIDER_WIDTH);
    const ratio = mooveX / SLIDER_WIDTH;
    stepValue.value = clamp(Math.round(ratio * 10), 0, 10);

    translateX.value = withTiming(
      (stepValue.value / NUMBER_OF_STEPS) * TOTAL_VALUE,
    );

    previousTranslateX.value =
      (stepValue.value / NUMBER_OF_STEPS) * TOTAL_VALUE;

    const labelValue = hasCustomValue
      ? numbers[stepValue.value]
      : stepValue.value;

    runOnJS(onValueChange)(labelValue);
  });

  const panGesture = Gesture.Pan()
    .onUpdate(event => {
      translateX.value = clamp(
        event.translationX + previousTranslateX.value,
        0,
        TOTAL_VALUE,
      );

      const ratio = translateX.value / TOTAL_VALUE;
      const slideStepValue = clamp(Math.round(ratio * 10), 0, 10);

      const labelValue = hasCustomValue
        ? numbers[slideStepValue]
        : slideStepValue;

      if (slideStepValue !== stepValue.value) {
        stepValue.value = slideStepValue;
        runOnJS(onValueChange)(labelValue);
      }
    })
    .onEnd(event => {
      const ratio = translateX.value / TOTAL_VALUE;

      stepValue.value = clamp(Math.round(ratio * 10), 0, 10);

      translateX.value = withTiming(
        (stepValue.value / NUMBER_OF_STEPS) * TOTAL_VALUE,
      );
      previousTranslateX.value =
        (stepValue.value / NUMBER_OF_STEPS) * TOTAL_VALUE;

      const labelValue = hasCustomValue
        ? numbers[stepValue.value]
        : stepValue.value;

      runOnJS(onValueChange)(labelValue);
    });

  const scrollTranslationStyle = useAnimatedStyle(() => {
    return { transform: [{ translateX: translateX.value }] };
  });

  const progressStyle = useAnimatedStyle(() => {
    return {
      width: translateX.value + KNOB_WIDTH,
    };
  });

  const numbersLabels = useMemo(() => {
    return numbers.map((el, i) => {
      const number = hasCustomValue ? minimumValue + i : i;
      return (
        <Animated.Text
          style={[styles.label, value === number && styles.labelSelected]}
          key={number}
        >
          {number}
        </Animated.Text>
      );
    });
  }, [value, numbers, hasCustomValue, minimumValue]);

  return (
    <Box
      width="100%"
      alignItems="center"
      justifyContent="center"
      flexDirection="column"
    >
      {hasLabels && (
        <SliderLabels
          sliderWidth={SLIDER_WIDTH}
          leftLabel={leftLabel}
          rightLabel={rightLabel}
        />
      )}
      <GestureDetector gesture={tapGesture}>
        <Animated.View style={[styles.container, style]}>
          <View style={styles.slider}>
            <Animated.View style={[styles.progress, progressStyle]} />
            <GestureDetector gesture={panGesture}>
              <Animated.View style={[styles.knob, scrollTranslationStyle]} />
            </GestureDetector>
          </View>
        </Animated.View>
      </GestureDetector>
      {hasNumbers && <View style={styles.numbers}>{numbersLabels}</View>}
    </Box>
  );
};

export { Slider };
